import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { goodsReceiptsApi, warehousesApi } from '../../api/warehouse';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { formatDate } from '../../utils/formatDate';

export const GOODS_RECEIPT_STATUS_LABELS: Record<string, string> = { draft: 'Draft', verified: 'Verified' };

interface CreateGrnForm {
  warehouseId: number; supplier?: string; purchaseOrderReference?: string; deliveryNoteReference?: string;
  receivedDate: string; receivedByEmployeeId?: number;
}

export function GoodsReceiptsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateGrnForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['goods-receipts'], queryFn: () => goodsReceiptsApi.list() });
  const { data: warehousesData } = useQuery({ queryKey: ['warehouses'], queryFn: () => warehousesApi.list() });
  const warehouses = warehousesData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (input: CreateGrnForm) => goodsReceiptsApi.create({ ...input, warehouseId: Number(input.warehouseId), receivingWarehouseId: Number(input.warehouseId) }),
    onSuccess: (result) => {
      showSuccess(`Goods receipt ${result.data.receiptReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['goods-receipts'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/goods-receipts/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const receipts = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Goods Receipts" description="Record goods received into a warehouse, from a purchase order, donation, or transfer." />

      <SectionHeader title="Receipts" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Goods Receipt'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Warehouse
            <select {...register('warehouseId', { required: true, valueAsNumber: true })}>
              <option value="">Select…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label>Supplier / Source <input {...register('supplier')} /></label>
          <label>Purchase Order Ref. <input {...register('purchaseOrderReference')} /></label>
          <label>Delivery Note Ref. <input {...register('deliveryNoteReference')} /></label>
          <label>Received Date <input type="date" {...register('receivedDate', { required: true })} /></label>
          <label>Received By <EmployeePicker {...register('receivedByEmployeeId', { valueAsNumber: true })} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create Draft</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load goods receipts. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={receipts}
          rowKey={(r) => r.id}
          columns={[
            { header: 'Reference', render: (r) => <a onClick={() => navigate(`/logistics/goods-receipts/${r.id}`)} className="table-link">{r.receipt_reference}</a> },
            { header: 'Supplier', render: (r) => r.supplier ?? '-' },
            { header: 'Received Date', render: (r) => formatDate(r.received_date) },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} label={GOODS_RECEIPT_STATUS_LABELS[r.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
